import React from "react";
import styled from "@emotion/styled";
import * as Tooltip from "@radix-ui/react-tooltip";

import { PORTAL_CONTAINER_ID } from "components/app_layout/TopLayerWrapper";
import KeyboardShortcut, { KeyboardShortcutProps } from "components/ui/kbd/Kbd";

export type TooltipComponentProps = {
  // Trigger. A single element, not a ReactNode — Radix renders the trigger with
  // `asChild`, so bare text, fragments and arrays have nothing to anchor to and
  // silently fail to show a tooltip.
  children: React.ReactElement;

  // Content
  content?: React.ReactNode | (() => React.ReactNode);
  keyboardShortcut?: KeyboardShortcutProps;
  "aria-label"?: string | undefined;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  variant?: "light" | "default";
  contentMaxWidth?: number;
  className?: string;

  // Provider
  delayDuration?: number;

  // Root
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export type TooltipVariant = "light" | "default";

export const DEFAULT_TOOLTIP_DELAY = 700;

/**
 * How long after a tooltip in the group closes that the next one still opens instantly.
 */
const SKIP_DELAY_DURATION = 300;

/**
 * Tracks whether a `TooltipGroup` is mounted above us, so an individual tooltip
 * knows whether it needs to provide its own Radix context.
 */
const TooltipGroupContext = React.createContext(false);

/**
 * Wrap a set of adjacent triggers — an icon button row, a toolbar — so the cursor can
 * move across them without re-waiting the full delay on each one. The first tooltip in
 * the group waits as normal; the rest open instantly while the cursor stays in the group.
 *
 * Deliberately opt-in and scoped. Radix shares this timing across a whole provider, so
 * mounting one at the app root would let a tooltip open instantly anywhere on the page
 * just because an unrelated one had recently been shown — which defeats the delay's
 * purpose of preventing accidental activation.
 *
 * Renders no DOM of its own, so it is safe to wrap around existing layout.
 */
export function TooltipGroup({ children }: { children: React.ReactNode }) {
  return (
    <TooltipGroupContext.Provider value={true}>
      <Tooltip.Provider
        delayDuration={DEFAULT_TOOLTIP_DELAY}
        skipDelayDuration={SKIP_DELAY_DURATION}
      >
        {children}
      </Tooltip.Provider>
    </TooltipGroupContext.Provider>
  );
}

/**
 * @param children - Trigger element
 * @param content - Tooltip content
 * @param keyboardShortcut - Optional keyboard shortcut to show in the tooltip
 * @param aria-label - Aria label for the tooltip content, if not set content is used **meaning it's rendered twice**
 * @param side - Position of the tooltip relative to the trigger
 * @param align - Alignment of the tooltip against the trigger along the side's axis
 * @param sideOffset - Offset of the tooltip from the trigger
 * @param variant - Style variant of the tooltip
 * @param contentMaxWidth - Maximum width of the tooltip content
 * @param delayDuration - Delay before the tooltip appears
 * @param open - Controlled open state of the tooltip, if not set state is managed internally
 * @param onOpenChange - Callback when the open state changes
 */
function TooltipComponent({
  children,
  content,
  keyboardShortcut,
  "aria-label": ariaLabel,
  contentMaxWidth = 280,
  side = "top",
  align = "center",
  sideOffset = 5,
  variant = "default",
  delayDuration = DEFAULT_TOOLTIP_DELAY,
  open,
  onOpenChange,
  className,
}: TooltipComponentProps) {
  const isInTooltipGroup = React.useContext(TooltipGroupContext);

  let contentToRender: React.ReactNode | undefined;

  if (typeof content === "function") {
    contentToRender = content();
  } else {
    contentToRender = content;
  }

  if (contentToRender == null) {
    return <>{children}</>;
  }

  // delayDuration lives on Root rather than Provider so a call site can still
  // override its own delay while sharing a group's skip-delay state.
  const tooltip = (
    <Tooltip.Root open={open} onOpenChange={onOpenChange} delayDuration={delayDuration}>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal container={getTooltipContainer()}>
        <StyledContent
          className={className}
          maxWidth={contentMaxWidth}
          aria-label={ariaLabel}
          side={side}
          align={align}
          sideOffset={sideOffset}
          variant={variant}
        >
          {contentToRender}
          {keyboardShortcut && <StyledKeyboardShortcut {...keyboardShortcut} variant={variant} />}
        </StyledContent>
      </Tooltip.Portal>
    </Tooltip.Root>
  );

  if (isInTooltipGroup) {
    return tooltip;
  }

  // Radix throws without a provider, so a standalone tooltip provides its own.
  // This is the common case: only tooltips inside a TooltipGroup share skip-delay state.
  return <Tooltip.Provider delayDuration={delayDuration}>{tooltip}</Tooltip.Provider>;
}

export default TooltipComponent;

// Use the existing top-level portal container which dynamically moves itself
// above dialogs/popovers, ensuring tooltips are never trapped in a lower stacking context.
const getTooltipContainer = () =>
  typeof document !== "undefined"
    ? document.getElementById(PORTAL_CONTAINER_ID) ?? undefined
    : undefined;

const StyledContent = styled(Tooltip.Content, {
  shouldForwardProp: (prop) => !["maxWidth"].includes(prop as string),
})<{ maxWidth: number; variant: TooltipVariant }>(({ theme, maxWidth, variant }) => ({
  maxWidth,
  whiteSpace: "pre-wrap",
  fontFamily: theme.fontFamily,
  fontSize: "14px",
  ...(variant === "light" && {
    backgroundColor: theme.colors.sysBackgroundDefault,
    padding: "16px",
    borderRadius: "8px",
    boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.12)",
  }),
  ...(variant === "default" && {
    padding: "12px",
    borderRadius: "4px",
    background: theme.colors.sysBackgroundInverse,
    color: theme.colors.sysTextWhite,
  }),
}));

const StyledKeyboardShortcut = styled(KeyboardShortcut)({
  marginLeft: "8px",
});
